import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Clapperboard, ShieldCheck, Users, Ban } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link, useNavigate } from 'react-router-dom';

const points = [
  { icon: Clapperboard, title: 'STAGED & FICTIONAL', text: 'Every case file, photoset and film is a scripted, staged production. No real events, persons, crimes or criminal activity are depicted.' },
  { icon: Users, title: 'CONSENTING ADULT PERFORMERS', text: 'All scenes are performed by consenting adults who own and operate this business. No minors appear anywhere on this site.' },
  { icon: ShieldCheck, title: 'NOT EXPLICIT', text: 'Suspense, not shock value. There is no nudity and no sexual content — the thrill comes from the story, the atmosphere and the mystery.' },
  { icon: Ban, title: 'NO REAL VIOLENCE OR INJURY', text: 'Dramatic moments are achieved through acting, props and editing. Nobody is harmed in the making of our content.' },
];

const ContentPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <motion.div className="min-h-screen bg-black" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} data-testid="content-policy-page">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Button variant="ghost" className="mb-6 text-amber-500 hover:text-amber-400 hover:bg-amber-900/20" onClick={() => navigate(-1)} data-testid="content-policy-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="bg-gray-900/90 border-2 border-amber-900/30 rounded-lg p-6 md:p-10">
          <h1 className="text-3xl font-headline text-amber-500 tracking-wider mb-2">CONTENT POLICY</h1>
          <p className="text-gray-500 font-typewriter text-sm mb-8">Last updated: October 2026</p>

          <p className="text-gray-300 font-typewriter leading-relaxed mb-8" data-testid="content-policy-statement">
            All content on Deadline by Daria is staged, fictional detective-themed entertainment performed by consenting
            adults. There is no nudity, sexual content, or real violence or injury.
            No minors appear on this site.
          </p>

          <p className="text-gray-400 font-typewriter leading-relaxed mb-8" data-testid="content-policy-inspiration">
            Our work is inspired by classic suspense cinema and the long tradition of television crime dramas and
            detective procedurals — whodunits, clever twists and atmospheric tension, told through photosets and short films.
          </p>

          <div className="grid gap-4 sm:grid-cols-2 mb-10">
            {points.map(({ icon: Icon, title, text }) => (
              <div key={title} className="border border-amber-900/30 bg-black/40 p-5 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-5 h-5 text-amber-500" />
                  <h2 className="font-headline text-amber-400 tracking-wider text-base">{title}</h2>
                </div>
                <p className="text-sm text-gray-400 font-typewriter leading-relaxed">{text}</p>
              </div>
            ))}
          </div>

          <section className="space-y-6 text-gray-300 font-typewriter leading-relaxed">
            <div>
              <h2 className="text-lg font-headline text-amber-400 tracking-wider mb-3">HOW WE MAKE OUR CONTENT</h2>
              <p>
                Our murder-mystery thriller films and detective-themed photosets are written, scripted and staged by the
                adult performers who own and operate Deadline by Daria. Think of it as a film set: every "crime scene"
                is a prop-dressed location, every "victim" is an actor, and every "case" is a story — in the spirit of
                the classic suspense films and TV crime procedurals we grew up watching.
              </p>
            </div>
            <div>
              <h2 className="text-lg font-headline text-amber-400 tracking-wider mb-3">AGE REQUIREMENT</h2>
              <p>
                This site is rated 18+ because of mature themes — suspense, dark storytelling and dramatic intensity —
                not because of explicit content. You must be 18 or older to create an account.
              </p>
            </div>
            <div>
              <h2 className="text-lg font-headline text-amber-400 tracking-wider mb-3">QUESTIONS</h2>
              <p>
                Email <a href="mailto:deadlinebydaria@gmail.com" className="text-amber-500 hover:text-amber-400 underline">deadlinebydaria@gmail.com</a>.
                See also our <Link to="/terms" className="text-amber-500 hover:text-amber-400 underline">Terms of Service</Link> and{' '}
                <Link to="/privacy" className="text-amber-500 hover:text-amber-400 underline">Privacy Policy</Link>.
              </p>
            </div>
          </section>
        </div>
      </div>
    </motion.div>
  );
};

export default ContentPolicyPage;
